import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';

/**
 * Pegar JSON crudo → inferir rutas (mismo motor que Explorar) → guardar en la columna.
 */
export default function JsonColumnExamplesDialog({
  open,
  onClose,
  tableId,
  column,
  initialExamples = [],
  initialPaths = [],
  getAuthHeaders,
  onSave,
}) {
  const [draft, setDraft] = useState('');
  const [examples, setExamples] = useState([]);
  const [paths, setPaths] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setExamples(Array.isArray(initialExamples) ? [...initialExamples] : []);
    setPaths(Array.isArray(initialPaths) ? [...initialPaths] : []);
    setDraft('');
    setError(null);
  }, [open, initialExamples, initialPaths]);

  const inferJsonStructureMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.post('/api/chart-builder/infer-json-structure', body, { headers }),
  });

  const runInfer = async (samples) => {
    const headers = getAuthHeaders?.();
    if (!headers) {
      setError('Sin autenticación');
      return null;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await inferJsonStructureMutation.mutateAsync({ body: { samples }, headers });
      if (!res.data.success) {
        setError(res.data.error || 'No se pudo inferir');
        return null;
      }
      setExamples(res.data.examples || []);
      setPaths(res.data.paths || []);
      return res.data;
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error al inferir');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    const next = [...examples];
    if (draft.trim()) next.push(draft.trim());
    if (!next.length) {
      setError('Pega al menos un JSON de ejemplo');
      return;
    }
    await runInfer(next);
    setDraft('');
  };

  const handleRemove = async (idx) => {
    const next = examples.filter((_, i) => i !== idx);
    if (!next.length) {
      setExamples([]);
      setPaths([]);
      return;
    }
    await runInfer(next);
  };

  const handleSave = () => {
    onSave?.({
      jsonExamples: examples,
      jsonPaths: paths,
      contentType: 'json',
    });
    onClose?.();
  };

  const handleClose = () => {
    if (draft.trim()) {
      if (!window.confirm('Tienes un JSON pegado sin añadir. Se va a perder si cierras. ¿Cerrar de todas formas?')) {
        return;
      }
    }
    onClose?.();
  };

  const rootKeys = [...new Set((paths || []).map((p) => String(p.path || '').split('.')[0]).filter(Boolean))];

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        Ejemplos JSON · {column}
        <Typography variant="caption" color="text.secondary" display="block">
          {tableId} — pega JSON crudo (p. ej. con <code>detections</code> u <code>objects</code>).
          La estructura se infiere; no hace falta listar rutas a mano.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          label="Pegar un JSON de ejemplo"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          multiline
          minRows={8}
          fullWidth
          placeholder='{ "detections": [ ... ] }'
          sx={{ mb: 1.5, fontFamily: 'monospace' }}
          inputProps={{ style: { fontFamily: 'ui-monospace, monospace', fontSize: 12 } }}
        />
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Button variant="contained" size="small" onClick={handleAdd} disabled={loading || !draft.trim()}>
            {loading ? <CircularProgress size={16} color="inherit" /> : 'Añadir e inferir'}
          </Button>
          {examples.length > 0 && (
            <Button size="small" variant="outlined" onClick={() => runInfer(examples)} disabled={loading}>
              Re-inferir
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        )}

        <Typography variant="subtitle2" gutterBottom>
          Ejemplos guardados ({examples.length})
        </Typography>
        {examples.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Sin ejemplos aún.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
            {examples.map((ex, i) => (
              <Box
                key={`ex-${ex}`}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  p: 1,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="caption"
                  component="pre"
                  sx={{
                    flex: 1,
                    m: 0,
                    maxHeight: 96,
                    overflow: 'auto',
                    fontFamily: 'ui-monospace, monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                  }}
                >
                  {ex.length > 400 ? `${ex.slice(0, 400)}…` : ex}
                </Typography>
                <IconButton size="small" onClick={() => handleRemove(i)} aria-label="Quitar ejemplo">
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        <Typography variant="subtitle2" gutterBottom>
          Estructura inferida
          {rootKeys.length > 0 && (
            <Typography component="span" variant="caption" color="text.secondary">
              {' '}
              · raíces: {rootKeys.join(', ')}
            </Typography>
          )}
        </Typography>
        {paths.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Añade ejemplos para ver rutas (arrays, campos, etc.).
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {paths.slice(0, 40).map((p) => (
              <Chip
                key={p.path}
                size="small"
                label={`${p.path}${(p.types || []).includes('array') ? '[]' : ''}`}
                color={(p.types || []).includes('array') ? 'primary' : 'default'}
                variant="outlined"
                sx={{ fontFamily: 'monospace', fontSize: 11 }}
              />
            ))}
            {paths.length > 40 && (
              <Chip size="small" label={`+${paths.length - 40} más`} />
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={loading}>
          Guardar en la columna
        </Button>
      </DialogActions>
    </Dialog>
  );
}
